import { resolveLanguageKey } from "@packages/core/language/language.ts";
import { useCachedManagerNames, useCachedSegmentNames } from "@domains/reviews/client/components/reviewee-filters/useCachedOptionNames.ts";
import { reviewCyclesI18NextInstanceKey } from "@domains/reviews/client/i18n/i18next.ts";
import { useCurrentLanguage, useI18nextInstance } from "@squide/i18next";
import { parseAsArrayOf, parseAsJson, parseAsString, parseAsStringEnum, useQueryStates } from "nuqs";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TENURE_BUCKET_LABELS } from "#shared/columnLabels.ts";
import { type ComparisonDimension, ComparisonDimensions } from "#shared/comparisonDimension.ts";
import { type ComparisonBreakdown, type ComparisonInput, type ComparisonSelection, ComparisonSelectionsSchema, differsOnlyByPeriod, flattenComparisonTree, MAX_COMPARISON_SELECTIONS, resolveTenureWindow, toComparisonInput } from "#shared/comparisonSelection.ts";
import { isFilterId } from "@domains/reviews/shared/reviewee-filters/memberFilters.ts";
import { TENURE_BUCKET_KEYS } from "@domains/reviews/shared/reviewee-filters/tenureBuckets.ts";
import { TrackOptions } from "@domains/reviews/shared/reviewee-filters/trackOptions.ts";
import { ReviewHistoryFilters } from "#shared/periodSelection.ts";
import { BASE_SERIES_ID } from "../components/comparison/comparisonSeries.ts";
import { type LabelledComparison, useComparisonSeries } from "../components/comparison/useComparisonSeries.ts";
import { i18NextInstanceKey } from "../i18n/i18next.ts";
import { resolveIdKeyedGroupLabel } from "./resolveIdKeyedGroupLabel.ts";
import { useAnalyticsFilters } from "./useAnalyticsFilters.ts";
import { useCycleLabels } from "./useCycleLabels.ts";
import { useManagerLabels } from "./useManagerLabels.ts";
import { useSegmentLabels } from "./useSegmentLabels.ts";
import { usePeriodLabel } from "./usePeriodLabel.ts";

const NO_COMPARISONS: ComparisonSelection[] = [];
const NO_GROUP_KEYS: string[] = [];

export const comparisonQuerySchema = {
    compareBy: parseAsStringEnum<ComparisonDimension>([...ComparisonDimensions]),
    compareGroups: parseAsArrayOf(parseAsString).withDefault(NO_GROUP_KEYS),
    compare: parseAsJson(value => ComparisonSelectionsSchema.parse(value)).withDefault(NO_COMPARISONS)
};

export const COMPARISON_QUERY_KEYS = Object.keys(comparisonQuerySchema);

const QUERY_STATE_OPTIONS = { history: "replace" as const, clearOnDefault: true, shallow: false };

function isUuidKeyedDimension(dimension: ComparisonDimension) {
    return dimension === "segment" || dimension === "manager" || dimension === "reportingLine";
}

function isPersonKeyedDimension(dimension: ComparisonDimension) {
    return dimension === "manager" || dimension === "reportingLine";
}

function toBreakdown(dimension: ComparisonDimension | null | undefined, groupKeys: string[]): ComparisonBreakdown | undefined {
    if (!dimension) {
        return undefined;
    }

    const usable = (isUuidKeyedDimension(dimension) ? groupKeys.filter(isFilterId) : groupKeys).slice(0, MAX_COMPARISON_SELECTIONS);

    return usable.length > 0 ? { dimension, groupKeys: usable } : undefined;
}

function toSanitizedComparisons(compare: ComparisonSelection[]): ComparisonSelection[] {
    return compare.map(comparison => ({ ...comparison, breakdown: comparison.breakdown && toBreakdown(comparison.breakdown.dimension, comparison.breakdown.groupKeys) }));
}

function countSeries(baseInput: ComparisonInput, baseBreakdown: ComparisonBreakdown | undefined, comparisons: ComparisonSelection[]): number {
    return flattenComparisonTree({ id: BASE_SERIES_ID, input: baseInput, breakdown: baseBreakdown }, comparisons).length;
}

export function useComparisonSelections() {
    const [{ compareBy, compareGroups, compare }, setQuery] = useQueryStates(comparisonQuerySchema, QUERY_STATE_OPTIONS);
    const { baseSelection } = useAnalyticsFilters();
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t: translateTrack } = useTranslation("TrackOptions", { i18n: useI18nextInstance(reviewCyclesI18NextInstanceKey) });
    const { t: translateCycles } = useTranslation("ReviewCycles", { i18n: i18nextInstance });
    const { t: translateGroups } = useTranslation("ComparisonGroups", { i18n: i18nextInstance });
    const describePeriodLabel = usePeriodLabel();
    const language = resolveLanguageKey(useCurrentLanguage());
    const managerNames = useCachedManagerNames();
    const segmentNames = useCachedSegmentNames();

    const baseBreakdown = useMemo(() => toBreakdown(compareBy, compareGroups), [compareBy, compareGroups]);
    const comparisonNodes = useMemo(() => toSanitizedComparisons(compare), [compare]);

    const cycleGroupKeys = useMemo(() => [baseBreakdown, ...comparisonNodes.map(node => node.breakdown)].flatMap(breakdown => (breakdown?.dimension === "cycle" ? breakdown.groupKeys : [])), [baseBreakdown, comparisonNodes]);
    const cycleLabels = useCycleLabels(cycleGroupKeys, language);

    const managerGroupKeys = useMemo(() => [baseBreakdown, ...comparisonNodes.map(node => node.breakdown)].flatMap(breakdown => (breakdown && isPersonKeyedDimension(breakdown.dimension) ? breakdown.groupKeys : [])), [baseBreakdown, comparisonNodes]);
    const managerLabels = useManagerLabels(managerGroupKeys);

    const segmentGroupKeys = useMemo(() => [baseBreakdown, ...comparisonNodes.map(node => node.breakdown)].flatMap(breakdown => (breakdown?.dimension === "segment" ? breakdown.groupKeys : [])), [baseBreakdown, comparisonNodes]);
    const segmentLabels = useSegmentLabels(segmentGroupKeys);

    const resolveGroupLabel = useCallback(
        (dimension: ComparisonDimension, groupKey: string) => {
            if (dimension === "cycle") {
                return cycleLabels.get(groupKey) || translateCycles("untitled");
            }

            if (dimension === "timeRange") {
                const preset = ReviewHistoryFilters.find(candidate => candidate === groupKey);

                return (preset && describePeriodLabel({ kind: "preset", preset })) || groupKey;
            }

            if (dimension === "tenure") {
                const band = TENURE_BUCKET_KEYS.find(key => key === groupKey);

                return (band && TENURE_BUCKET_LABELS[band]?.[language]) || groupKey;
            }

            if (dimension === "track") {
                const option = TrackOptions.find(candidate => candidate === groupKey);

                return option ? translateTrack(option) : groupKey;
            }

            if (isPersonKeyedDimension(dimension)) {
                return resolveIdKeyedGroupLabel(groupKey, managerLabels, managerNames, translateGroups("unknownPerson"));
            }

            if (dimension === "segment") {
                return resolveIdKeyedGroupLabel(groupKey, segmentLabels, segmentNames, translateGroups("unknownSegment"));
            }

            return groupKey;
        },
        [language, translateTrack, translateCycles, translateGroups, describePeriodLabel, cycleLabels, segmentLabels, segmentNames, managerLabels, managerNames]
    );

    const flattened = useMemo<LabelledComparison[]>(
        () => flattenComparisonTree({ id: BASE_SERIES_ID, input: baseSelection, breakdown: baseBreakdown }, comparisonNodes).map(entry => (entry.kind === "group" ? { ...entry, label: resolveGroupLabel(entry.dimension, entry.groupKey) } : entry)),
        [baseSelection, baseBreakdown, comparisonNodes, resolveGroupLabel]
    );

    const series = useComparisonSeries(flattened);

    const comparisons = useMemo<ComparisonSelection[]>(() => flattened.map(entry => ({ id: entry.id, label: entry.label, ...entry.input })), [flattened]);

    const comparisonInputs = useMemo<ComparisonInput[]>(() => {
        const now = new Date();

        return flattened.map(entry => resolveTenureWindow(entry.input, now));
    }, [flattened]);

    const isPerSubjectComparable = useMemo(() => differsOnlyByPeriod([baseSelection, ...comparisonInputs]), [baseSelection, comparisonInputs]);

    const duplicateComparison = useCallback(
        (sourceId: string) =>
            void setQuery(current => {
                const nodes = toSanitizedComparisons(current.compare ?? NO_COMPARISONS);

                if (countSeries(baseSelection, toBreakdown(current.compareBy, current.compareGroups ?? NO_GROUP_KEYS), nodes) >= MAX_COMPARISON_SELECTIONS) {
                    return {};
                }

                const source = nodes.find(node => node.id === sourceId);
                const copied = sourceId === BASE_SERIES_ID ? baseSelection : source ? toComparisonInput(source) : {};

                return { compare: [...nodes, { ...copied, id: crypto.randomUUID() }] };
            }),
        [setQuery, baseSelection]
    );

    const updateComparison = useCallback((id: string, selection: ComparisonInput) => void setQuery(current => ({ compare: (current.compare ?? NO_COMPARISONS).map(node => (node.id === id ? { ...selection, id, breakdown: node.breakdown } : node)) })), [setQuery]);

    const removeComparison = useCallback(
        (id: string) =>
            void setQuery(current => {
                const remaining = (current.compare ?? NO_COMPARISONS).filter(node => node.id !== id);

                return { compare: remaining.length > 0 ? remaining : null };
            }),
        [setQuery]
    );

    const setBreakdown = useCallback(
        (nodeId: string, dimension: ComparisonDimension, groupKeys: string[]) =>
            void setQuery(current => {
                const nodes = toSanitizedComparisons(current.compare ?? NO_COMPARISONS);
                const isBase = nodeId === BASE_SERIES_ID;

                const spentElsewhere = countSeries(baseSelection, isBase ? undefined : toBreakdown(current.compareBy, current.compareGroups ?? NO_GROUP_KEYS), isBase ? nodes : nodes.map(node => (node.id === nodeId ? { ...node, breakdown: undefined } : node)));
                const chosen = groupKeys.slice(0, Math.max(0, MAX_COMPARISON_SELECTIONS - spentElsewhere));

                if (isBase) {
                    return { compareBy: chosen.length > 0 ? dimension : null, compareGroups: chosen.length > 0 ? chosen : null };
                }

                return { compare: nodes.map(node => (node.id === nodeId ? { ...node, breakdown: chosen.length > 0 ? { dimension, groupKeys: chosen } : undefined } : node)) };
            }),
        [setQuery, baseSelection]
    );

    const clearComparisons = useCallback(() => void setQuery({ compareBy: null, compareGroups: null, compare: null }), [setQuery]);

    const usedSeriesCount = flattened.length;

    return useMemo(
        () => ({
            comparisons,
            comparisonInputs,
            isPerSubjectComparable,
            baseBreakdown,
            comparisonNodes,
            series,
            usedSeriesCount,
            maxSeriesCount: MAX_COMPARISON_SELECTIONS,
            remainingSeriesSlots: Math.max(0, MAX_COMPARISON_SELECTIONS - usedSeriesCount),
            resolveGroupLabel,
            duplicateComparison,
            updateComparison,
            removeComparison,
            clearComparisons,
            setBreakdown
        }),
        [comparisons, comparisonInputs, isPerSubjectComparable, baseBreakdown, comparisonNodes, series, usedSeriesCount, resolveGroupLabel, duplicateComparison, updateComparison, removeComparison, clearComparisons, setBreakdown]
    );
}
